import { Skeleton, SkeletonRegion } from "@/components/ui/skeleton";
import { useEffect, useState } from "react";
import { ExpenseCategory } from "@boo/shared";
import { TrendingUp } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { CATEGORY_LABELS } from "../../monthly-summary/types/category.types";
import { useBudgetStore } from "../store/useBudgetStore";

const EXPENSE_CATEGORIES = Object.values(ExpenseCategory);

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function fmt(n: number) {
  if (n >= 1_000_000) return `฿${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `฿${(n / 1_000).toFixed(0)}K`;
  return `฿${n}`;
}

export default function BudgetTrendChart() {
  const [selectedCategory, setSelectedCategory] = useState<
    ExpenseCategory | ""
  >("");
  const { trend, trendLoading, loadTrend } = useBudgetStore(
    useShallow((s) => ({
      trend: s.trend,
      trendLoading: s.trendLoading,
      loadTrend: s.loadTrend,
    })),
  );

  useEffect(() => {
    loadTrend(6, selectedCategory || undefined);
  }, [selectedCategory, loadTrend]);

  const points = trend?.points ?? [];
  const maxValue = Math.max(...points.flatMap((p) => [p.budget, p.actual]), 1);

  return (
    <div className="rounded-2xl bg-card border border-border p-4 sm:p-6 shadow-card">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-[15px] font-semibold text-foreground">
              Budget Trend
            </h2>
          </div>
          <p className="text-[13px] text-muted-foreground">
            Budget vs actual over the last 6 months
          </p>
        </div>

        <div className="relative">
          <select
            value={selectedCategory}
            onChange={(e) =>
              setSelectedCategory(e.target.value as ExpenseCategory | "")
            }
            className="text-[13px] rounded-lg border border-border bg-background text-foreground pl-3 pr-8 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/30 appearance-none cursor-pointer"
          >
            <option value="">All categories</option>
            {EXPENSE_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {CATEGORY_LABELS[cat]}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 4l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </div>

      {trendLoading && (
        <SkeletonRegion className="space-y-3 py-2">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-4" style={{ width: `${85 - i * 15}%` }} />
            </div>
          ))}
        </SkeletonRegion>
      )}

      {!trendLoading && points.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 gap-2">
          <TrendingUp className="w-8 h-8 text-muted-foreground/40" />
          <p className="text-[13px] text-muted-foreground">
            No budget or spending data for this range.
          </p>
        </div>
      )}

      {!trendLoading && points.length > 0 && (
        <div className="space-y-5">
          {/* Legend */}
          <div className="flex items-center gap-5 text-[12px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-muted-foreground/30" />
              Budget
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-primary" />
              Actual
            </span>
          </div>

          {/* Chart rows */}
          <div className="space-y-4">
            {points.map((point) => {
              const budgetPct = (point.budget / maxValue) * 100;
              const actualPct = (point.actual / maxValue) * 100;
              const isOver = point.budget > 0 && point.actual > point.budget;
              const monthLabel = `${MONTH_NAMES[point.month - 1]} ${String(point.year).slice(2)}`;

              return (
                <div key={`${point.year}-${point.month}`}>
                  <p className="text-[12px] font-medium text-muted-foreground mb-1.5 [font-variant-numeric:tabular-nums]">
                    {monthLabel}
                  </p>

                  {/* Budget bar */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] text-muted-foreground w-10 text-right shrink-0">
                      Budget
                    </span>
                    <div className="flex-1 h-4 bg-muted rounded-sm overflow-hidden">
                      <div
                        className="h-full rounded-sm bg-muted-foreground/25 transition-all duration-300"
                        style={{ width: `${budgetPct}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-muted-foreground w-14 text-right shrink-0 [font-variant-numeric:tabular-nums]">
                      {point.budget > 0 ? fmt(point.budget) : "—"}
                    </span>
                  </div>

                  {/* Actual bar */}
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground w-10 text-right shrink-0">
                      Actual
                    </span>
                    <div className="flex-1 h-4 bg-muted rounded-sm overflow-hidden">
                      <div
                        className={`h-full rounded-sm transition-all duration-300 ${
                          isOver ? "bg-destructive" : "bg-primary"
                        }`}
                        style={{ width: `${actualPct}%` }}
                      />
                    </div>
                    <span
                      className={`text-[11px] w-14 text-right shrink-0 [font-variant-numeric:tabular-nums] ${
                        isOver
                          ? "text-destructive font-medium"
                          : "text-muted-foreground"
                      }`}
                    >
                      {point.actual > 0 ? fmt(point.actual) : "—"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
